/**
 * dashboardEditChat.ts — Edit-session API client.
 *
 * Plain async functions wrapping the two edit-session endpoints the FE
 * needs after the chat-driven confirmation pivot (2026-05-11):
 *  - bootstrapEditSession — open a session for a dashboard
 *  - sendChatMessage      — send a user reply; the AI decides confirm/cancel
 *
 * The BE retains `/apply` and `/cancel` HTTP endpoints for
 * programmatic / CLI use, but the FE no longer calls them — confirmation
 * is now routed through chat tool calls instead.
 *
 * Follows the same auth + base-URL + error-handling pattern as the rest of
 * src/API/*.
 *
 * Success bodies are raw (no TCSAPIResponse envelope) — same convention as
 * DashboardProposedPanels.ts, DashboardFlowTransition.ts, and Dashboards.ts
 * buildDashboard (CEO 2026-04-28 directive: new endpoints skip the envelope).
 *
 * Era: dashboard-era only. All dashboardId args reference dashboard-era sessions.
 *
 * TDD ref: plans/2026-05-07-edit-dashboard-via-ai-tdd.md §5.5
 */

import axios, { AxiosError, AxiosResponse } from 'axios'
import { config } from '@/config'
import { formatResponse } from './helpers/formatResponse'
import type {
  BootstrapEditSessionResponse,
  SendEditChatMessageResponse,
} from '@/types/dashboardEditChat.types'

export const DashboardEditChatAPI = {
  /**
   * POST /v1/dashboards/:id/edit-sessions
   *
   * Bootstraps a new edit session for the given dashboard. If a prior edit
   * session is open for this (user, dashboard) pair, it is archived by the
   * backend and its id is included in `archivedPriorSessionIds`.
   *
   * Returns `{ sessionId, initialFlowState, initialFlowContext, archivedPriorSessionIds }`.
   */
  bootstrapEditSession: async (
    token: string,
    dashboardId: string
  ): Promise<{
    data: BootstrapEditSessionResponse | undefined
    err: string | undefined
    status: number | undefined
    errorCode: string | undefined
  }> => {
    try {
      const resp: AxiosResponse<BootstrapEditSessionResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/dashboards/${encodeURIComponent(dashboardId)}/edit-sessions`,
        headers: { Authorization: `Bearer ${token}` },
        data: {},
      })
      return {
        data: resp.data,
        err: undefined,
        status: resp.status,
        errorCode: undefined,
      }
    } catch (error) {
      const axiosErr = error as AxiosError<{ error?: string; reason?: string }>
      const status = axiosErr.response?.status
      const body = axiosErr.response?.data
      const errorCode = body?.error ?? body?.reason
      const formatted = formatResponse<BootstrapEditSessionResponse>(null, axiosErr)
      return {
        data: formatted.data,
        err: formatted.err,
        status,
        errorCode,
      }
    }
  },

  /**
   * POST /v1/dashboards/:id/edit-sessions/:sessionId/messages
   *
   * Sends a user chat message to an active edit session. The backend runs
   * `generateChatResponse` against the edit session (NOT the create session),
   * so any mutation tool call the AI emits is dispatched to
   * `DashboardEditService.proposeMutation` and the session's
   * `flow_context.pendingMutation` is persisted.
   *
   * On `mutation_proposed` responses, the message object carries the updated
   * `flowState` and `flowContext` so the FE can keep
   * `dashboardEditChatSlice` in sync without a second round-trip.
   */
  sendChatMessage: async (
    token: string,
    dashboardId: string,
    sessionId: string,
    message: string
  ): Promise<{
    data: SendEditChatMessageResponse | undefined
    err: string | undefined
    status: number | undefined
    errorCode: string | undefined
  }> => {
    try {
      const resp: AxiosResponse<SendEditChatMessageResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/dashboards/${encodeURIComponent(dashboardId)}/edit-sessions/${encodeURIComponent(sessionId)}/messages`,
        headers: { Authorization: `Bearer ${token}` },
        data: { message },
      })
      return {
        data: resp.data,
        err: undefined,
        status: resp.status,
        errorCode: undefined,
      }
    } catch (error) {
      const axiosErr = error as AxiosError<{ error?: string; reason?: string }>
      const status = axiosErr.response?.status
      const body = axiosErr.response?.data
      const errorCode = body?.error ?? body?.reason
      const formatted = formatResponse<SendEditChatMessageResponse>(null, axiosErr)
      return {
        data: formatted.data,
        err: formatted.err,
        status,
        errorCode,
      }
    }
  },

}
